import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AuthService } from '@igo2/auth';
import { Tool } from '@igo2/common/tool';
import { ConfigService } from '@igo2/core/config';
import { LanguageService } from '@igo2/core/language';
import { Message, MessageService } from '@igo2/core/message';
import { RouteService } from '@igo2/core/route';
import { StorageService } from '@igo2/core/storage';
import type { AnyLayer, IgoMap, Layer } from '@igo2/geo';
import { ExportService, isLayerGroup } from '@igo2/geo';
import { ObjectUtils, uuid } from '@igo2/utils';

import GeoJSON from 'ol/format/GeoJSON';
import { Geometry } from 'ol/geom';
import olPoint from 'ol/geom/Point';
import Cluster from 'ol/source/Cluster';
import olVectorSource from 'ol/source/Vector';

import { Feature } from 'ol';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  first,
  map,
  mergeMap,
  skip,
  tap
} from 'rxjs/operators';

import { ShareMapService } from '../../share-map/shared/share-map.service';
import {
  Context,
  ContextDetailedChanges,
  ContextMapView,
  ContextProfils,
  ContextServiceOptions,
  ContextsList,
  DetailedContext,
  ExtraFeatures
} from './context.interface';

@Injectable({
  providedIn: 'root'
})
export class ContextService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private languageService = inject(LanguageService);
  private config = inject(ConfigService);
  private messageService = inject(MessageService);
  private storageService = inject(StorageService);
  private exportService = inject(ExportService);
  private shareMapService = inject(ShareMapService);
  private route = inject(RouteService, { optional: true });

  public context$ = new BehaviorSubject<DetailedContext>(undefined);
  public contexts$ = new BehaviorSubject<ContextsList>({ ours: [] });
  public defaultContextId$ = new BehaviorSubject<number | string>(undefined);
  public editedContext$ = new BehaviorSubject<DetailedContext>(undefined);
  public importedContext: DetailedContext[] = [];
  public toolsChanged$ = new Subject<DetailedContext>();
  private mapViewFromRoute: ContextMapView = {};
  private options: ContextServiceOptions;
  private baseUrl: string;

  // Until the ContextService is completely refactored, this is needed
  // to track the current tools
  private tools: Tool[];
  private toolbar: string[];

  get defaultContextUri(): string {
    return (
      (this.storageService.get('favorite.context.uri') as string) ||
      this._defaultContextUri ||
      this.options.defaultContextUri
    );
  }
  set defaultContextUri(uri: string) {
    this._defaultContextUri = uri;
  }
  private _defaultContextUri: string;

  constructor() {
    this.options = Object.assign(
      {
        basePath: 'contexts',
        contextListFile: '_contexts.json',
        defaultContextUri: '_default'
      },
      this.config.getConfig('context')
    );

    this.baseUrl = this.options.url ?? '';

    if (this.authService.hasAuthService) {
      this.authService.logged$.subscribe((logged) => {
        if (logged) {
          this.contexts$.pipe(skip(1), first()).subscribe(() => {
            this.handleContextsChange();
          });
          this.loadContexts();
        }
      });
    } else {
      this.loadContexts();
      this.handleContextsChange(false);
    }
  }

  get(permissions?: string[], hidden?: boolean): Observable<ContextsList> {
    let url = this.baseUrl + '/contexts';
    if (permissions && this.authService.authenticated) {
      url += '?permission=' + permissions.join();
      if (hidden) {
        url += '&hidden=true';
      }
    }
    return this.http.get<ContextsList>(url);
  }

  getById(id: string): Observable<Context> {
    const url = this.baseUrl + '/contexts/' + id;
    return this.http.get<Context>(url);
  }

  getDetails(id: number): Observable<DetailedContext> {
    const url = `${this.baseUrl}/contexts/${id}/details`;
    return this.http.get<DetailedContext>(url).pipe(
      catchError((res) => {
        this.handleError(res, id);
        throw res;
      })
    );
  }

  getDetailsByUri(uri: string): Observable<DetailedContext> {
    const url = `${this.baseUrl}/contexts/uri/${uri}/details`;
    return this.http.get<DetailedContext>(url).pipe(
      catchError((res) => {
        this.handleError(res, uri);
        throw res;
      })
    );
  }

  getDefault(): Observable<DetailedContext> {
    if (this.authService.authenticated) {
      const url = this.baseUrl + '/contexts/default';
      return this.http.get<DetailedContext>(url).pipe(
        tap((context) => {
          this.defaultContextId$.next(context.id);
        })
      );
    } else {
      const uri = this.storageService.get('favorite.context.uri') as string;
      this.defaultContextId$.next(uri);
      return this.getContextByUri(uri);
    }
  }

  getProfilByUser(): Observable<ContextProfils[]> {
    if (this.baseUrl) {
      const url = this.baseUrl + '/profils?';
      return this.http.get<ContextProfils[]>(url);
    }
    return of([]);
  }

  setDefault(id: string | number): Observable<string | number | undefined> {
    if (this.authService.authenticated) {
      const url = this.baseUrl + '/contexts/default';
      return this.http.post<string>(url, { defaultContextId: id });
    } else {
      return this.setDefaultLocalStorage(id);
    }
  }

  private setDefaultLocalStorage(
    id: string | number
  ): Observable<string | number | undefined> {
    const selectedId = this.storageService.get('favorite.context.uri');
    if (selectedId === id) {
      this.storageService.remove('favorite.context.uri');
    } else {
      this.storageService.set('favorite.context.uri', id);
    }
    return of(selectedId === id ? undefined : id);
  }

  hideContext(id: number) {
    const url = this.baseUrl + '/contexts/' + id + '/hide';
    return this.http.post(url, {});
  }

  showContext(id: number) {
    const url = this.baseUrl + '/contexts/' + id + '/show';
    return this.http.post(url, {});
  }

  delete(id: number, imported = false): Observable<void> {
    const contexts: ContextsList = { ours: [] };
    Object.keys(this.contexts$.value).forEach(
      (key) =>
        (contexts[key] = this.contexts$.value[key].filter((c) => c.id !== id))
    );

    if (imported) {
      this.importedContext = this.importedContext.filter((c) => c.id !== id);
      return of(this.contexts$.next(contexts));
    }

    const url = this.baseUrl + '/contexts/' + id;
    return this.http.delete<void>(url).pipe(
      tap(() => {
        this.contexts$.next(contexts);
        this.loadContext(this.defaultContextUri);
      })
    );
  }

  create(context: DetailedContext): Observable<Context> {
    const url = this.baseUrl + '/contexts';
    return this.http.post<Context>(url, context).pipe(
      map((contextCreated) => {
        if (this.authService.authenticated) {
          contextCreated.permission = 'write';
        } else {
          contextCreated.permission = 'read';
        }
        this.contexts$.value.ours.unshift(contextCreated);
        this.contexts$.next(this.contexts$.value);
        return contextCreated;
      })
    );
  }

  clone(id: number, properties = {}): Observable<Context> {
    const url = this.baseUrl + '/contexts/' + id + '/clone';
    return this.http.post<Context>(url, properties).pipe(
      map((contextCloned) => {
        contextCloned.permission = 'write';
        this.contexts$.value.ours.unshift(contextCloned);
        this.contexts$.next(this.contexts$.value);
        return contextCloned;
      })
    );
  }

  update(
    id: number,
    context: DetailedContext
  ): Observable<ContextDetailedChanges> {
    const url = this.baseUrl + '/contexts/' + id;
    return this.http.patch<ContextDetailedChanges>(url, context);
  }

  // =================================================================

  addToolAssociation(contextId: string, toolId: string): Observable<void> {
    const url = `${this.baseUrl}/contexts/${contextId}/tools`;
    const association = {
      toolId
    };
    return this.http.post<void>(url, association);
  }

  deleteToolAssociation(
    contextId: string,
    toolId: string
  ): Observable<unknown> {
    const url = `${this.baseUrl}/contexts/${contextId}/tools/${toolId}`;
    return this.http.delete(url);
  }

  // ======================================================================

  getLocalContexts(): Observable<ContextsList> {
    const url = this.getPath(this.options.contextListFile);
    return this.http.get<Context[]>(url).pipe(
      map((res) => {
        return { ours: res } satisfies ContextsList;
      })
    );
  }

  getLocalContext(uri: string): Observable<DetailedContext> {
    const url = this.getPath(`${uri}.json`);
    return this.http.get<DetailedContext>(url).pipe(
      mergeMap((res) => {
        if (!res.base) {
          return of(res);
        }
        const urlBase = this.getPath(`${res.base}.json`);
        return this.http.get<DetailedContext>(urlBase).pipe(
          map((resBase: DetailedContext) => {
            const resMerge = res;
            resMerge.map = ObjectUtils.mergeDeep(resBase.map, res.map);
            resMerge.layers = (resBase.layers || [])
              .concat(res.layers || [])
              .reverse()
              .filter(
                (l, index, self) =>
                  !l.id || self.findIndex((l2) => l2.id === l.id) === index
              )
              .reverse();
            resMerge.toolbar = res.toolbar || resBase.toolbar;
            resMerge.message = res.message || resBase.message;
            resMerge.messages = res.messages || resBase.messages;
            resMerge.tools = (res.tools || [])
              .concat(resBase.tools || [])
              .filter(
                (t, index, self) =>
                  self.findIndex((t2) => t2.name === t.name) === index
              );
            return resMerge;
          }),
          catchError((err) => {
            this.handleError(err, uri);
            throw err;
          })
        );
      }),
      catchError((err2) => {
        this.handleError(err2, uri);
        throw err2;
      })
    );
  }

  loadContexts(permissions?: string[], hidden?: boolean) {
    let request;
    if (this.baseUrl) {
      request = this.get(permissions, hidden);
    } else {
      request = this.getLocalContexts();
    }
    request.subscribe((contexts) => {
      contexts.ours = this.importedContext.concat(contexts.ours);
      this.contexts$.next(contexts);
    });
  }

  loadDefaultContext() {
    const loadFct = (direct = false) => {
      if (!direct && this.baseUrl && this.authService.authenticated) {
        this.getDefault().subscribe({
          next: (_context) => {
            this.defaultContextUri = _context.uri;
            this.addContextToList(_context);
            this.setContext(_context);
          },
          error: () => {
            this.defaultContextId$.next(undefined);
            this.loadContext(this.defaultContextUri);
          }
        });
      } else {
        this.loadContext(this.defaultContextUri);
      }
    };

    if (this.route) {
      this.route.queryParams.pipe(debounceTime(100)).subscribe((params) => {
        const contextParam = this.shareMapService.getContext(params);
        let direct = false;
        if (contextParam) {
          this.defaultContextUri = contextParam;
          direct = true;
        }
        loadFct(direct);
      });
    } else {
      loadFct();
    }
  }

  loadContext(uri: string) {
    const context = this.context$.value;

    if (context && context.uri === uri) {
      return;
    }

    this.getContextByUri(uri)
      .pipe(first())
      .subscribe(
        (_context: DetailedContext) => {
          this.addContextToList(_context);
          this.setContext(_context);
        },
        () => {
          if (uri !== this.options.defaultContextUri) {
            this.loadContext(this.options.defaultContextUri);
          }
        }
      );
  }

  setContext(context: DetailedContext) {
    this.handleContextMessage(context);
    const currentContext = this.context$.value;
    if (currentContext && context && context.id === currentContext.id) {
      if (context.map.view.keepCurrentView === undefined) {
        context.map.view.keepCurrentView = true;
      }
      this.context$.next(context);
      return;
    }

    if (!context.map) {
      context.map = { view: {} };
    }

    Object.assign(context.map.view, this.mapViewFromRoute);

    this.context$.next(context);
  }

  loadEditedContext(uri: string) {
    this.getContextByUri(uri).subscribe((_context: DetailedContext) => {
      this.setEditedContext(_context);
    });
  }

  setEditedContext(context: DetailedContext) {
    this.editedContext$.next(context);
  }

  getContextFromMap(igoMap: IgoMap, empty?: boolean): DetailedContext {
    const view = igoMap.ol.getView();
    const proj = view.getProjection().getCode();
    const center = new olPoint(view.getCenter()).transform(proj, 'EPSG:4326');

    const context: DetailedContext = {
      uri: uuid(),
      title: '',
      scope: 'private',
      map: {
        view: {
          center: center.getCoordinates() as [number, number],
          zoom: view.getZoom(),
          projection: proj,
          maxZoomOnExtent: igoMap.viewController.maxZoomOnExtent
        }
      },
      layers: [],
      tools: []
    };

    const layers: AnyLayer[] = empty
      ? []
      : [...igoMap.layerController.treeLayers];

    const baseLayer = igoMap.layerController.baseLayer;
    if (baseLayer) {
      layers.unshift(baseLayer);
    }

    // Remove null for the first level, the LayerGroup is already doing that for it's children
    context.layers = layers
      .map((layer) => layer.saveableOptions)
      .filter(Boolean);

    context.tools = this.tools.map((tool) => {
      return {
        id: tool.id,
        name: tool.name,
        global: tool.global
      };
    });

    return context;
  }

  getContextFromLayers(
    igoMap: IgoMap,
    layers: AnyLayer[],
    name: string,
    keepCurrentView?: boolean
  ): DetailedContext {
    const view = igoMap.ol.getView();
    const proj = view.getProjection().getCode();
    const center = new olPoint(view.getCenter()).transform(proj, 'EPSG:4326');

    const context: DetailedContext = {
      uri: name,
      title: name,
      map: {
        view: {
          center: center.getCoordinates() as [number, number],
          zoom: view.getZoom(),
          projection: proj,
          keepCurrentView
        }
      },
      layers: [],
      toolbar: [],
      tools: [],
      extraFeatures: []
    };

    context.layers = igoMap.layerController.baseLayers.map((l: Layer) => {
      return {
        baseLayer: true,
        sourceOptions: l.options.sourceOptions,
        title: l.options.title,
        visible: l.visible
      };
    });

    layers.forEach((layer) => {
      if (isLayerGroup(layer)) {
        return context.layers.push(layer.options);
      }
      if (!(layer.ol.getSource() instanceof olVectorSource)) {
        const layerOptions = layer.options;
        layerOptions.zIndex = layer.zIndex;
        layerOptions.visible = layer.visible;
        layerOptions.opacity = layer.opacity;
        delete layerOptions.source;
        context.layers.push(layerOptions);
      } else {
        const extraFeatures = this.getExtraFeatures(layer);
        extraFeatures.name = layer.options.title;
        extraFeatures.opacity = layer.opacity;
        extraFeatures.visible = layer.visible;
        context.extraFeatures.push(extraFeatures);
      }
    });

    context.toolbar = this.toolbar;
    context.tools = this.tools;

    return context;
  }

  private getExtraFeatures(layer: Layer): ExtraFeatures {
    const writer = new GeoJSON();
    let olFeatures: Feature<Geometry>[];
    if (layer.ol.getSource() instanceof Cluster) {
      const clusterSource = layer.ol.getSource() as Cluster;
      olFeatures = clusterSource.getFeatures();
      olFeatures = olFeatures.flatMap((cluster) => cluster.get('features'));
    } else {
      const source = layer.ol.getSource() as olVectorSource;
      olFeatures = source.getFeatures();
    }
    const cleanedOlFeatures = this.exportService.cleanFeatures(
      olFeatures,
      'GeoJSON',
      '_featureStore'
    );
    const features = writer.writeFeatures(cleanedOlFeatures, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857'
    });
    return JSON.parse(features);
  }

  setTools(tools: Tool[]) {
    this.tools = tools;
  }

  setToolbar(toolbar: string[]) {
    this.toolbar = toolbar;
  }

  private handleContextMessage(context: DetailedContext) {
    if (
      this.context$.value &&
      context.uri &&
      this.context$.value.uri !== context.uri
    ) {
      this.messageService.removeAllAreNotError();
    }

    context.messages = context.messages ? context.messages : [];
    context.messages.push(context.message);
    context.messages.map((message) => {
      if (message) {
        this.messageService.message(message as Message);
      }
    });
  }

  private getContextByUri(uri: string): Observable<DetailedContext> {
    if (this.baseUrl) {
      let contextToLoad;
      for (const key of Object.keys(this.contexts$.value)) {
        contextToLoad = this.contexts$.value[key].find((c) => {
          return c.uri === uri;
        });
        if (contextToLoad) {
          break;
        }
      }

      if (contextToLoad && contextToLoad.imported) {
        return of(contextToLoad);
      }

      return contextToLoad
        ? this.getDetails(contextToLoad.id)
        : this.getDetailsByUri(uri);
    }

    const importedContext = this.contexts$.value.ours.find((currentContext) => {
      return currentContext.uri === uri && currentContext.imported === true;
    });

    if (importedContext) {
      return of(importedContext);
    } else {
      return this.getLocalContext(uri);
    }
  }

  getContextLayers(map: IgoMap) {
    return map.layerController.treeLayers;
  }

  private getPath(file: string) {
    const basePath = this.options.basePath.replace(/\/$/, '');

    return `${basePath}/${file}`;
  }

  private handleError(
    error: HttpErrorResponse,
    uri: string | number,
    permissionError?: boolean
  ) {
    const context = this.contexts$.value.ours.find((obj) => obj.uri === uri);
    const titleContext = context ? context.title : uri;
    error.error.title = this.languageService.translate.instant(
      'igo.context.contextManager.invalid.title'
    );

    error.error.message = this.languageService.translate.instant(
      'igo.context.contextManager.invalid.text',
      { value: titleContext }
    );

    error.error.toDisplay = true;

    if (permissionError) {
      error.error.title = this.languageService.translate.instant(
        'igo.context.contextManager.errors.addPermissionTitle'
      );
      error.error.message = this.languageService.translate.instant(
        'igo.context.contextManager.errors.addPermission'
      );
    }
    this.messageService.error(
      'igo.context.contextManager.errors.addPermission',
      'igo.context.contextManager.errors.addPermissionTitle'
    );
  }

  private handleContextsChange(keepCurrentContext = true) {
    const context = this.context$.value;
    const editedContext = this.editedContext$.value;
    if (!context || context.uri === this.options.defaultContextUri) {
      keepCurrentContext = false;
    }
    if (!keepCurrentContext || !this.findContext(context)) {
      this.defaultContextUri = undefined;
      this.loadDefaultContext();
    } else {
      this.getContextByUri(context.uri)
        .pipe(first())
        .subscribe((newContext) => {
          this.toolsChanged$.next(newContext);
        });

      if (this.baseUrl && this.authService.authenticated) {
        this.getDefault().subscribe();
      }
    }
    const editedFound = this.findContext(editedContext);
    if (!editedFound || editedFound.permission !== 'write') {
      this.setEditedContext(undefined);
    }
  }

  private addContextToList(context: Context) {
    const contextFound = this.findContext(context);
    if (!contextFound) {
      const contextSimplifie: Context = {
        id: context.id,
        uri: context.uri,
        title: context.title,
        scope: context.scope,
        permission: 'read'
      };

      if (this.contexts$.value && this.contexts$.value.public) {
        this.contexts$.value.public.push(contextSimplifie);
        this.contexts$.next(this.contexts$.value);
      }
    }
  }

  private findContext(context: Context) {
    if (!context) {
      return false;
    }

    const contexts = this.contexts$.value;
    let found;
    for (const key of Object.keys(contexts)) {
      const value = contexts[key];
      found = value.find(
        (c) =>
          (context.id && c.id === context.id) ||
          (context.uri && c.uri === context.uri)
      );
      if (found) {
        break;
      }
    }

    return found;
  }
}
